import { useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { focusNextAfterChoice, nextFocusable } from "@/lib/focusNext";
import { requirementKeys, listConsignmentRequirements, type ConsignmentRequirement } from "./orders.api";
import { requirementOptions, type RequirementOption } from "./requirementOptions";
import { STOP_REQUIREMENTS, requirementLabelKey } from "@/features/locations/requirements";

// What a consignment asks of the transport — kooiaap, diervoeder, temperatuur -7 (S47,
// renamed from "statuses" at S50). The list itself is kept under Beheer, so this only shows
// what is in use. A consignment takes them in one field beside its goods (RequirementField,
// on a new order and on the order card); the relation card, where they are the customer's
// own defaults, still shows them all as chips to switch on and off.

/** The list as everyone sees it, cached for the session: it changes rarely. */
export function useConsignmentRequirements() {
  return useQuery({
    queryKey: requirementKeys.list("true"),
    queryFn: () => listConsignmentRequirements("true"),
    staleTime: 5 * 60_000,
  });
}

/**
 * The requirement ADR: once it is ticked, order entry types the ADR code beside it (orderentry, 20-10-2026). Found by
 * its code, in the list or among what the consignment has; null while the list loads.
 */
export function useAdrRequirementId(known?: ConsignmentRequirement[]): string | null {
  const query = useConsignmentRequirements();
  return [...(query.data ?? []), ...(known ?? [])].find((requirement) => requirement.code === "ADR")?.id ?? null;
}

export function RequirementChips({ requirements }: { requirements: ConsignmentRequirement[] }) {
  if (requirements.length === 0) return null;
  return (
    <div className="wl-hstack" style={{ gap: "4px", flexWrap: "wrap" }}>
      {requirements.map((requirement) => (
        <span key={requirement.id} className="wl-chip" title={requirement.description ?? undefined}>
          {requirement.name}
        </span>
      ))}
    </div>
  );
}

export function RequirementPicker({
  id,
  selected,
  onChange,
  label,
  help,
}: {
  id: string;
  selected: string[];
  onChange: (next: string[]) => void;
  label?: string;
  help?: string;
}) {
  const { t } = useTranslation();
  const query = useConsignmentRequirements();
  const requirements = query.data ?? [];
  const chosen = new Set(selected);

  function toggle(requirementId: string) {
    onChange(chosen.has(requirementId) ? selected.filter((value) => value !== requirementId) : [...selected, requirementId]);
  }

  return (
    <div className="wl-vstack" style={{ gap: "8px", alignItems: "stretch" }}>
      <div className="wl-hstack" style={{ gap: "8px", alignItems: "baseline" }}>
        <span className="wl-field__label" id={`${id}-label`} style={{ margin: 0 }}>{label ?? t("orders.requirementsLabel")}</span>
        <span className="t-caption t-muted">
          {selected.length > 0 ? t("orders.requirementsChosen", { count: selected.length }) : t("orders.requirementsNoneChosen")}
        </span>
      </div>
      {query.isLoading && <span className="t-caption t-muted">{t("common.loading")}</span>}
      {!query.isLoading && requirements.length === 0 && <span className="t-caption t-muted">{t("orders.requirementsNone")}</span>}
      <div className="wl-hstack" role="group" aria-labelledby={`${id}-label`} style={{ gap: "8px", flexWrap: "wrap" }}>
        {requirements.map((requirement) => {
          const on = chosen.has(requirement.id);
          return (
            <button
              key={requirement.id}
              type="button"
              aria-pressed={on}
              title={requirement.description ?? undefined}
              className="wl-chip"
              // Chosen or not has to read at a glance (Camiel, 17-09-2026): a chosen one is
              // navy with a tick, the rest is an empty outline on the card's own surface.
              style={{
                cursor: "pointer",
                height: "28px",
                padding: "0 12px",
                fontSize: "var(--fs-sm)",
                background: on ? "var(--action-primary-bg)" : "var(--surface-base)",
                color: on ? "var(--text-on-brand)" : "var(--text-secondary)",
                borderColor: on ? "var(--action-primary-bg)" : "var(--border-default)",
              }}
              onClick={() => toggle(requirement.id)}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ opacity: on ? 1 : 0.25 }}>
                {on ? <path d="M20 6 9 17l-5-5" /> : <circle cx="12" cy="12" r="9" />}
              </svg>
              {requirement.name}
            </button>
          );
        })}
      </div>
      {help && <span className="t-caption t-muted">{help}</span>}
    </div>
  );
}

/**
 * The transport requirements of a consignment as one field (Camiel, 21-09-2026:
 * "goederenomschrijving kan korter zodat de vervoerseisen er naast passen en veel minder
 * ruimte kosten"). Nine chips took two lines and a help text under the goods; this is as
 * high as the fields beside it. What is chosen stands in it, the rest opens under it: click
 * or arrow down for the list, type to find one. Enter takes the highlighted one and stays for
 * the next, Tab takes it and moves on as in the other pickers, and Backspace in an empty
 * field takes the last one off again.
 */
export function RequirementField({
  id,
  selected,
  onChange,
  known = [],
}: {
  id: string;
  selected: string[];
  onChange: (next: string[]) => void;
  /** Names for chosen ones the list may no longer have: switched off in Beheer since. */
  known?: ConsignmentRequirement[];
}) {
  const { t } = useTranslation();
  const query = useConsignmentRequirements();
  return (
    <ChoiceField
      id={id}
      options={query.data ?? []}
      known={known}
      loading={query.isLoading}
      emptyText={t("orders.requirementsNone")}
      listLabel={t("orders.requirementsLabel")}
      selected={selected}
      onChange={onChange}
    />
  );
}

/**
 * What one stop asks of the vehicle or the driver (S49), in the same field as the transport
 * requirements (Camiel, 21-09-2026: "Vervoerseisen moet je nu typen en eis erbij kun je
 * kiezen uit een lijst. maak deze hetzelfde"). What the place itself asks stands in it grey
 * and cannot be taken off here; that is changed on the location.
 */
export function StopRequirementField({
  id,
  selected,
  onChange,
  fromLocation = [],
}: {
  id: string;
  selected: string[];
  onChange: (next: string[]) => void;
  fromLocation?: string[];
}) {
  const { t } = useTranslation();
  const name = (requirement: string) => {
    const key = requirementLabelKey(requirement);
    return key ? String(t(key as never)) : requirement;
  };
  const options = STOP_REQUIREMENTS.map((requirement) => ({ id: requirement, code: requirement, name: name(requirement) }));
  return (
    <ChoiceField
      id={id}
      options={options}
      fixed={fromLocation.map((requirement) => ({ id: requirement, name: name(requirement), title: t("orders.fromLocation") }))}
      listLabel={t("orders.stopRequirements")}
      ariaLabel={t("orders.stopRequirements")}
      placeholder={t("orders.stopRequirementsPlaceholder")}
      selected={selected}
      onChange={onChange}
    />
  );
}

interface ChoiceOption extends RequirementOption {
  description?: string | null;
}

/**
 * Chosen values as chips in one field, the rest to find by typing or to pick from the list
 * under it. Enter takes the highlighted one and stays for the next, Tab takes it and moves
 * on, Backspace in an empty field takes the last one off, Escape closes only the list.
 */
export function ChoiceField({
  id,
  options: all,
  selected,
  onChange,
  known = [],
  fixed = [],
  loading = false,
  emptyText,
  listLabel,
  placeholder,
  ariaLabel,
}: {
  id: string;
  options: ChoiceOption[];
  selected: string[];
  onChange: (next: string[]) => void;
  /** Names for chosen values the options no longer have. */
  known?: ChoiceOption[];
  /** Shown first, grey, and not to be taken off here. */
  fixed?: { id: string; name: string; title?: string }[];
  loading?: boolean;
  /** When there are no options at all. */
  emptyText?: string;
  listLabel: string;
  placeholder?: string;
  /** Without a label above it, as in the table of stops. */
  ariaLabel?: string;
}) {
  const { t } = useTranslation();
  const query = { isLoading: loading };
  const [text, setText] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const wrapper = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);

  const byId = new Map<string, ChoiceOption>([...known, ...all].map((option) => [option.id, option]));
  const chosen = selected.flatMap((requirementId) => {
    const requirement = byId.get(requirementId);
    return requirement ? [requirement] : [];
  });
  // What the place already asks is not offered again.
  const options = requirementOptions(all, [...selected, ...fixed.map((item) => item.id)], text);
  const listId = `${id}-list`;
  // Once everything is chosen there is nothing to open; a search that finds nothing says so.
  const showList = open && (query.isLoading || all.length === 0 || options.length > 0 || text.trim() !== "");

  function add(requirementId: string) {
    onChange([...selected, requirementId]);
    setText("");
    setActive(-1);
  }

  function remove(requirementId: string) {
    onChange(selected.filter((value) => value !== requirementId));
    input.current?.focus();
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      setActive((index) => Math.min(index + 1, options.length - 1));
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((index) => Math.max(index - 1, 0));
      return;
    }
    if (e.key === "Escape" && showList) {
      // Closes the list only, not the screen it is on.
      e.preventDefault();
      e.stopPropagation();
      setOpen(false);
      setActive(-1);
      return;
    }
    if ((e.key === "Enter" || (e.key === "Tab" && !e.shiftKey)) && showList && active >= 0) {
      const option = options[active];
      if (!option) return;
      e.preventDefault();
      if (e.key === "Tab") {
        const next = nextFocusable(wrapper.current);
        add(option.id);
        setOpen(false);
        focusNextAfterChoice(next);
      } else {
        add(option.id);
      }
      return;
    }
    if (e.key === "Enter") {
      // An Enter in this field is meant for it, never for a form around it.
      e.preventDefault();
      return;
    }
    if (e.key === "Backspace" && text === "" && selected.length > 0) {
      e.preventDefault();
      onChange(selected.slice(0, -1));
    }
  }

  return (
    <div ref={wrapper} style={{ position: "relative", minWidth: 0 }}>
      <div
        className="wl-tokenfield"
        // Without the mail field's padding it is exactly as high as the inputs beside it.
        style={{ paddingTop: 0, paddingBottom: 0 }}
        // Beside the chips still puts the caret in the field, and opens the list.
        onMouseDown={(e) => {
          if (e.target === e.currentTarget) {
            e.preventDefault();
            input.current?.focus();
            setOpen(true);
          }
        }}
      >
        {fixed.map((item) => (
          <span key={`fixed-${item.id}`} className="wl-token" title={item.title} style={{ background: "var(--surface-sunken)", color: "var(--text-secondary)", paddingRight: "var(--space-2)" }}>
            <span className="wl-token__text">{item.name}</span>
          </span>
        ))}
        {chosen.map((requirement) => (
          <span key={requirement.id} className="wl-token" title={requirement.description ?? undefined}>
            <span className="wl-token__text">{requirement.name}</span>
            <button
              type="button"
              tabIndex={-1}
              className="wl-token__remove"
              aria-label={t("orders.requirementRemove", { name: requirement.name })}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => remove(requirement.id)}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M18 6 6 18M6 6l12 12" />
              </svg>
            </button>
          </span>
        ))}
        <input
          id={id}
          ref={input}
          className="wl-tokenfield__input"
          // Room to type the next one without pushing it onto a line of its own: two
          // requirements and the caret still fit on one line of a field this narrow.
          style={chosen.length + fixed.length > 0 ? { flexBasis: "var(--space-12)", minWidth: "var(--space-12)" } : undefined}
          type="text"
          role="combobox"
          autoComplete="off"
          aria-label={ariaLabel}
          aria-expanded={showList}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={showList && active >= 0 ? `${listId}-${active}` : undefined}
          placeholder={chosen.length + fixed.length === 0 ? (placeholder ?? t("orders.requirementsPlaceholder")) : undefined}
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            setOpen(true);
            // Typing narrows the list and points at the first that fits, so Enter takes it.
            setActive(e.target.value.trim() === "" ? -1 : 0);
          }}
          onFocus={() => setOpen(true)}
          onClick={() => setOpen(true)}
          onBlur={() => {
            setOpen(false);
            setActive(-1);
            // What was typed and not taken means nothing once the field is left.
            setText("");
          }}
          onKeyDown={onKeyDown}
        />
      </div>
      {showList && (
        <ul
          id={listId}
          role="listbox"
          aria-label={listLabel}
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            zIndex: "var(--z-overlay)",
            maxHeight: "calc(var(--control-height-md) * 8)",
            overflowY: "auto",
            margin: "var(--space-1) 0 0",
            padding: "var(--space-1)",
            listStyle: "none",
            background: "var(--surface-raised)",
            border: "1px solid var(--border-default)",
            borderRadius: "var(--radius-md)",
            boxShadow: "var(--shadow-md)",
          }}
        >
          {query.isLoading && <li className="t-caption t-muted" style={{ padding: "var(--space-1_5) var(--space-2)" }}>{t("common.loading")}</li>}
          {!query.isLoading && all.length === 0 && (
            <li className="t-caption t-muted" style={{ padding: "var(--space-1_5) var(--space-2)" }}>
              {emptyText ?? t("orders.requirementsNone")}
            </li>
          )}
          {!query.isLoading && all.length > 0 && options.length === 0 && (
            <li className="t-caption t-muted" style={{ padding: "var(--space-1_5) var(--space-2)" }}>
              {t("orders.requirementsNothingFound")}
            </li>
          )}
          {options.map((option, index) => (
            <li
              key={option.id}
              id={`${listId}-${index}`}
              role="option"
              aria-selected={index === active}
              className="t-body-sm"
              // Keeps the focus in the field, and the list open for the next one.
              onMouseDown={(e) => {
                e.preventDefault();
                add(option.id);
              }}
              onMouseEnter={() => setActive(index)}
              style={{
                padding: "var(--space-1_5) var(--space-2)",
                borderRadius: "var(--radius-sm)",
                cursor: "pointer",
                background: index === active ? "var(--surface-hover)" : undefined,
              }}
            >
              {option.name}
              {option.description && <span className="t-caption t-muted"> · {option.description}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
